import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Container, Form, FormGroup, Label, Input, Button, UncontrolledAlert } from 'reactstrap';
import { Loading } from '../../components/Loading';
import { getCustomGames, postAddCustomGame, putModifyCustomGame, getPlaces } from '../../services/api';
import { Scorer } from '../../components/Scorer';

const emptyForm = {
    name1: '',
    name2: '',
    score1: '',
    score2: '',
    logo1: '',
    logo2: '',
    status: '',
    category: '',
    date: '',
    urlVideo: '',
    comments: '',
};

const toStoredFormat = (datetimeLocal) => {
    if (!datetimeLocal) return '';
    const [datePart, timePart] = datetimeLocal.split('T');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year} - ${timePart}`;
};

const toDatetimeLocal = (stored) => {
    if (!stored || stored.length < 18) return ''; 
    const day = stored.substring(0, 2);
    const month = stored.substring(3, 5);
    const year = stored.substring(6, 10);
    const time = stored.substring(13);
    return `${year}-${month}-${day}T${time}`;
};

export function CustomGameForm() {
    const { id } = useParams();
    const history = useHistory();
    const isEditing = !!id && id !== 'new';
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(isEditing);
    const [saved, setSaved] = useState(false);
    const [places, setPlaces] = useState([]);

    useEffect(() => {
        getPlaces().then(data => setPlaces(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!isEditing) return;
        getCustomGames().then(games => {
            const game = games.find(g => g.id === id);
            if (game) {
                setForm({
                    name1: game.name1 || '',
                    name2: game.name2 || '',
                    score1: game.score1 != null ? game.score1 : '',
                    score2: game.score2 != null ? game.score2 : '',
                    logo1: game.logo1 || '',
                    logo2: game.logo2 || '',
                    status: game.status || '',
                    category: game.category || '',
                    date: game.date ? toDatetimeLocal(game.date) : '',
                    urlVideo: game.urlVideo || '',
                    comments: game.comments || '',
                });
            }
            setLoading(false);
        });
    }, [id, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const saveGame = async (updatedForm) => {
        if (!isEditing) return;
        const game = {
            ...updatedForm,
            id,
            date: toStoredFormat(updatedForm.date),
            score1: updatedForm.score1 !== '' ? parseInt(updatedForm.score1) : null,
            score2: updatedForm.score2 !== '' ? parseInt(updatedForm.score2) : null,
        };
        await putModifyCustomGame(game);
    };

    const handleScoreChange = (field, delta) => {
        setForm(prev => {
            const current = prev[field] !== '' ? parseInt(prev[field]) : 0;
            const next = field === 'score1' || field === 'score2'
                ? Math.max(0, current + delta)
                : current + delta;
            const updated = { ...prev, [field]: next };
            saveGame(updated);
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const game = {
            ...form,
            date: toStoredFormat(form.date),
            score1: form.score1 !== '' ? parseInt(form.score1) : null,
            score2: form.score2 !== '' ? parseInt(form.score2) : null,
        };
        if (isEditing) {
            game.id = id;
            await putModifyCustomGame(game);
        } else {
            await postAddCustomGame(game);
        }
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
    };

    if (loading) {
        return <Loading />;
    }

    const previewMatch = {
        name1: form.name1 || 'Equipo 1',
        name2: form.name2 || 'Equipo 2',
        score1: form.score1 !== '' ? parseInt(form.score1) : null,
        score2: form.score2 !== '' ? parseInt(form.score2) : null,
        logo1: form.logo1 || '/images/hockey_logo.jpeg',
        logo2: form.logo2 || '/images/hockey_logo.jpeg',
        category: form.category,
        date: toStoredFormat(form.date),
        status: form.status,
        urlVideo: form.urlVideo,
    };

    return (
        <Container>
            <div style={{
                position: 'sticky',
                top: 80,
                zIndex: 10,
                backgroundColor: '#fcfbfc',
                borderBottom: '1px solid #dee2e6',
                padding: '10px 0',
                marginBottom: '1rem',
            }}>
                <Scorer match={previewMatch} hideTitle={false} />
            </div>
            <h2>{isEditing ? 'Editar partido' : 'Nuevo partido'}</h2>
            <Form onSubmit={handleSubmit}>
                <FormGroup>
                    <Label for="name1">Equipo 1</Label>
                    <Input id="name1" name="name1" value={form.name1} onChange={handleChange} required />
                </FormGroup>
                <FormGroup>
                    <Label for="name2">Equipo 2</Label>
                    <Input id="name2" name="name2" value={form.name2} onChange={handleChange} required />
                </FormGroup>
                <FormGroup>
                    <Label>Goles equipo 1</Label>
                    <div className="d-flex align-items-center gap-2">
                        <Button type="button" color="secondary" onClick={() => handleScoreChange('score1', -1)}>−</Button>
                        <span style={{ minWidth: '2rem', textAlign: 'center', fontSize: '1.2rem' }}>{form.score1 !== '' ? form.score1 : '—'}</span>
                        <Button type="button" color="secondary" onClick={() => handleScoreChange('score1', 1)}>+</Button>
                        <Button type="button" color="link" className="text-danger p-0 ms-2" onClick={() => setForm(prev => ({ ...prev, score1: '' }))}>Reset</Button>
                    </div>
                </FormGroup>
                <FormGroup>
                    <Label>Goles equipo 2</Label>
                    <div className="d-flex align-items-center gap-2">
                        <Button type="button" color="secondary" onClick={() => handleScoreChange('score2', -1)}>−</Button>
                        <span style={{ minWidth: '2rem', textAlign: 'center', fontSize: '1.2rem' }}>{form.score2 !== '' ? form.score2 : '—'}</span>
                        <Button type="button" color="secondary" onClick={() => handleScoreChange('score2', 1)}>+</Button>
                        <Button type="button" color="link" className="text-danger p-0 ms-2" onClick={() => setForm(prev => ({ ...prev, score2: '' }))}>Reset</Button>
                    </div>
                </FormGroup>
                <FormGroup>
                    <Label for="logo1">Logo equipo 1 (URL)</Label>
                    {places.length > 0 && (
                        <Input type="select" className="mb-1" value={form.logo1}
                            onChange={e => e.target.value && setForm(prev => ({ ...prev, logo1: e.target.value }))}>
                            <option value="">— Seleccionar logo —</option>
                            <option value="/images/hockey_logo.jpeg">Default</option>
                            {places.map(p => <option key={p.teamName} value={p.teamImageUrl}>{p.teamName}</option>)}
                        </Input>
                    )}
                    <Input id="logo1" name="logo1" value={form.logo1} onChange={handleChange} placeholder="O escribe la URL" />
                </FormGroup>
                <FormGroup>
                    <Label for="logo2">Logo equipo 2 (URL)</Label>
                    {places.length > 0 && (
                        <Input type="select" className="mb-1" value={form.logo2}
                            onChange={e => e.target.value && setForm(prev => ({ ...prev, logo2: e.target.value }))}>
                            <option value="">— Seleccionar logo —</option>
                            <option value="/images/hockey_logo.jpeg">Default</option>
                            {places.map(p => <option key={p.teamName} value={p.teamImageUrl}>{p.teamName}</option>)}
                        </Input>
                    )}
                    <Input id="logo2" name="logo2" value={form.logo2} onChange={handleChange} placeholder="O escribe la URL" />
                </FormGroup>
                <FormGroup>
                    <Label for="status">Estado</Label>
                    <Input id="status" name="status" value={form.status} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                    <Label for="category">Categoría</Label>
                    <Input id="category" name="category" value={form.category} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                    <Label for="date">Fecha</Label>
                    <Input id="date" name="date" type="datetime-local" value={form.date} onChange={handleChange} onBlur={handleChange} />
                </FormGroup>
                <FormGroup>
                    <Label for="urlVideo">URL Vídeo</Label>
                    <Input id="urlVideo" name="urlVideo" value={form.urlVideo} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                    <Label for="comments">Comentarios</Label>
                    <Input id="comments" name="comments" type="textarea" value={form.comments} onChange={handleChange} />
                </FormGroup>
                {saved && (
                    <UncontrolledAlert color="success" fade={true}>
                        {isEditing ? 'Partido guardado' : 'Partido creado'}
                    </UncontrolledAlert>
                )}
                <div style={{ marginBottom: '2rem' }}>
                    <Button color="primary" type="submit">{isEditing ? 'Guardar cambios' : 'Crear partido'}</Button>
                    {' '}
                    <Button color="secondary" type="button" onClick={() => history.push('/customgames')}>Cerrar</Button>
                </div>
            </Form>
        </Container>
    );
}
